// Faz BO (2026-05-10) — Yeni sidebar
// UI/UX brief §4. Gruplandırılmış, daraltılabilir (full ↔ mini), role-based,
// koyu lacivert sidebar token'larıyla; mobile drawer; aktif state sol border.
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Badge,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import { useNavMenu } from "../../hooks/useNavMenu";
import { sidebarTokens } from "../../theme/tokens";

export const SIDEBAR_FULL_WIDTH = 264;
export const SIDEBAR_MINI_WIDTH = 72;

function MenuItem({ item, badge, isActive, isMini, onClick }) {
  const button = (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: isMini ? 0 : 2,
        py: 1.1,
        mx: isMini ? 1 : 1,
        my: 0.25,
        borderRadius: 1,
        cursor: "pointer",
        color: isActive ? sidebarTokens.fgActive : sidebarTokens.fg,
        bgcolor: isActive ? sidebarTokens.bgActive : "transparent",
        fontWeight: isActive ? 600 : 400,
        fontSize: 13,
        justifyContent: isMini ? "center" : "flex-start",
        transition: "background-color 120ms cubic-bezier(0.4,0,0.2,1), color 120ms",
        "&:hover": { bgcolor: sidebarTokens.bgHover, color: sidebarTokens.fgActive },
        "&::before": isActive ? {
          content: '""',
          position: "absolute",
          left: 0,
          top: 6,
          bottom: 6,
          width: 3,
          borderRadius: 2,
          backgroundColor: "primary.main",
        } : undefined,
      }}
    >
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 20,
        height: 20,
        flexShrink: 0,
        "& svg": { fontSize: 20 },
      }}>
        {badge > 0 ? (
          <Badge badgeContent={badge} color="error" sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}>
            {item.icon}
          </Badge>
        ) : item.icon}
      </Box>
      {!isMini && (
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.label}
        </Box>
      )}
      {!isMini && badge > 0 && (
        <Box sx={{
          ml: 1, px: 1, py: 0.25, borderRadius: 999, fontSize: 10, fontWeight: 700,
          bgcolor: "error.main", color: "common.white", lineHeight: 1.2,
        }}>
          {badge > 99 ? "99+" : badge}
        </Box>
      )}
    </Box>
  );

  if (isMini) {
    return (
      <Tooltip
        title={badge > 0 ? `${item.label} (${badge})` : item.label}
        placement="right"
        arrow
        enterDelay={300}
      >
        {button}
      </Tooltip>
    );
  }
  return button;
}

function SectionLabel({ children, isMini }) {
  if (isMini) {
    return <Box sx={{ height: 1, mx: 2, my: 1, bgcolor: sidebarTokens.border }} />;
  }
  return (
    <Typography
      sx={{
        px: 2,
        mt: 2,
        mb: 0.5,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: sidebarTokens.sectionFg,
      }}
    >
      {children}
    </Typography>
  );
}

function SidebarContent({ isMini, onItemClick, onCollapseToggle, mobileMode, onMobileClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { sections, badges } = useNavMenu();

  const handleClick = (path) => {
    navigate(path);
    onItemClick?.();
  };

  return (
    <Box sx={{
      width: "100%",
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      bgcolor: sidebarTokens.bg,
      color: sidebarTokens.fg,
      borderRight: `1px solid ${sidebarTokens.border}`,
    }}>
      {/* Brand */}
      <Box sx={{
        height: 64,
        px: isMini ? 0 : 2,
        display: "flex",
        alignItems: "center",
        justifyContent: isMini ? "center" : "space-between",
        borderBottom: `1px solid ${sidebarTokens.border}`,
        flexShrink: 0,
      }}>
        {!isMini ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.2,
              bgcolor: "primary.main", color: "primary.contrastText",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontWeight: 800, fontSize: 14, flexShrink: 0,
            }}>M</Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ fontSize: 14, fontWeight: 700, color: sidebarTokens.fgActive, lineHeight: 1.2 }}>
                Miksan B2B
              </Box>
              <Box sx={{ fontSize: 10, color: sidebarTokens.sectionFg }}>Yönetim Paneli</Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.2,
            bgcolor: "primary.main", color: "primary.contrastText",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 800, fontSize: 14,
          }}>M</Box>
        )}
        {mobileMode && (
          <IconButton size="small" onClick={onMobileClose} sx={{ color: sidebarTokens.fg }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Menu */}
      <Box sx={{
        flex: 1,
        overflowY: "auto",
        py: 1,
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(255,255,255,0.12)", borderRadius: 3 },
      }}>
        {sections.map((group, idx) => (
          <Box key={group.section + idx}>
            <SectionLabel isMini={isMini}>{group.section}</SectionLabel>
            {group.items.map(item => (
              <MenuItem
                key={item.path}
                item={item}
                isActive={location.pathname === item.path}
                isMini={isMini}
                badge={item.badgeKey ? (badges[item.badgeKey] || 0) : 0}
                onClick={() => handleClick(item.path)}
              />
            ))}
          </Box>
        ))}
      </Box>

      {/* Footer — collapse button (sadece desktop) */}
      {onCollapseToggle && !mobileMode && (
        <Box sx={{
          borderTop: `1px solid ${sidebarTokens.border}`,
          p: 1,
          display: "flex",
          justifyContent: isMini ? "center" : "flex-end",
          flexShrink: 0,
        }}>
          <Tooltip title={isMini ? "Genişlet" : "Daralt"} placement="top">
            <IconButton size="small" onClick={onCollapseToggle} sx={{ color: sidebarTokens.fg }}>
              {isMini ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

export default function Sidebar({ isMini, onCollapseToggle, mobileOpen, onMobileClose }) {
  return (
    <>
      {/* Desktop / Tablet — sabit drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: isMini ? SIDEBAR_MINI_WIDTH : SIDEBAR_FULL_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: isMini ? SIDEBAR_MINI_WIDTH : SIDEBAR_FULL_WIDTH,
            boxSizing: "border-box",
            border: "none",
            transition: "width 200ms cubic-bezier(0.4,0,0.2,1)",
            overflow: "hidden",
          },
        }}
      >
        <SidebarContent isMini={isMini} onCollapseToggle={onCollapseToggle} />
      </Drawer>

      {/* Mobile — temporary drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: SIDEBAR_FULL_WIDTH, boxSizing: "border-box", border: "none" },
        }}
      >
        <SidebarContent
          isMini={false}
          mobileMode
          onMobileClose={onMobileClose}
          onItemClick={onMobileClose}
        />
      </Drawer>
    </>
  );
}
